import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc } from "firebase/firestore";
import { DATABASE } from "../../../../Config/firebase";
import Swal from "sweetalert2";
import axios from "axios";

const SafeDeleteVerificationTool = () => {
  const [loading, setLoading] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [allTransactions, setAllTransactions] = useState([]); // All transactions before filtering
  const [filteredTransactions, setFilteredTransactions] = useState([]); // Filtered transactions
  const [usersMap, setUsersMap] = useState(new Map());
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Filter and sort state
  const [statusFilter, setStatusFilter] = useState("all"); // all, 1-4 (active), or specific status
  const [typeFilter, setTypeFilter] = useState("all"); // all, 0 (Deposit), 1 (Withdrawal), 2 (Performance)
  const [collectionFilter, setCollectionFilter] = useState("all"); // all, transection_requests, ledgers (if related)
  const [searchText, setSearchText] = useState(""); // Search by user name or ID
  const [sortBy, setSortBy] = useState("date"); // date, userName, amount, status
  const [sortOrder, setSortOrder] = useState("desc"); // asc, desc

  const verifySafeToDelete = async () => {
    setLoading(true);
    setVerificationResult(null);
    setAllTransactions([]);
    setFilteredTransactions([]);

    try {
      // OPTIONAL: CUTOFF_DATE for "recent" vs "old" classification (for display purposes only)
      // User wants to see ALL transactions including from 2022, so we still fetch all
      // but categorize them for display
      const CUTOFF_DATE = new Date('2024-12-01T00:00:00Z'); // December 1, 2024
      
      // Fetch ALL transactions (including from 2022) - no date filter
      console.log('📥 Fetching ALL transactions from database (including 2022)...');
      const allTransactionsSnapshot = await getDocs(collection(DATABASE, "transection_requests"));
      console.log(`✅ Fetched ${allTransactionsSnapshot.docs.length} total transactions`);
      
      // Load users before processing transactions
      await loadUsers();
      
      const processedTransactions = [];
      const recentActive = []; // Transactions from 12/2024 onwards that are truly active (block deletion)
      const oldStuck = []; // Old transactions (before 12/2024) that are active - these are stuck/test data
      const missingUserIds = new Set();
      
      // Process ALL transactions to show everything
      allTransactionsSnapshot.docs.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        const status = data.request_status;
        const date = data.date?.toDate ? data.date.toDate() : (data.date ? new Date(data.date) : null);
        const isRecent = date && date >= CUTOFF_DATE; // Only for categorization, not filtering
        const userId = data.user_id || "N/A";
        
        // Validate user_id - Firebase UIDs are typically 28 characters, not timestamps
        // If user_id looks like a timestamp (all digits, > 12 digits), it might be wrong
        const isValidUserId = userId && userId !== "N/A" && 
                              typeof userId === 'string' && 
                              userId.length > 10 && // Firebase UIDs are long
                              !(/^\d{13,}$/.test(userId)); // Not a timestamp (13+ digits)
        
        // Debug specific document mentioned by user
        if (docSnapshot.id === 'rlhMCzzm7eB0i1GlMQuX') {
          console.log(`🔍 [DEBUG] Processing document rlhMCzzm7eB0i1GlMQuX:`, {
            id: docSnapshot.id,
            request_status: status,
            request_status_type: typeof status,
            request_status_value: String(status),
            request_type: data.request_type,
            approved: data.approved,
            is_approved: data.is_approved,
            notes: data.notes,
            request_status_raw: data.request_status,
            allFields: Object.keys(data),
            fullRawData: JSON.stringify(data, null, 2).substring(0, 1000)
          });
        }
        
        // Track missing users (only if it's a valid user_id format)
        if (isValidUserId && !usersMap.has(userId)) {
          missingUserIds.add(userId);
        } else if (userId && userId !== "N/A" && !isValidUserId) {
          // Log potential invalid user_id (might be a document ID or timestamp)
          console.warn(`⚠️ [USER_ID] Potentially invalid user_id format in transaction ${docSnapshot.id}:`, {
            user_id: userId,
            date: date?.toISOString(),
            document_id: docSnapshot.id,
            allFields: Object.keys(data)
          });
        }
        
        // Check if there's a related ledger (check ledgers collection by doc_id)
        const hasRelatedLedger = data.doc_id ? true : false; // If doc_id exists, likely has ledger
        
        // CRITICAL: Determine request_type first (needed for status label and isActive calculation)
        const requestType = data.request_type !== undefined ? Number(data.request_type) : null;
        
        // CRITICAL: Determine if transaction is "active" based on request_type
        // For DEPOSITS (request_type: 0): Only status 0 (Pending) or 1 (Waiting) are active
        // Status 2 (Approved) is finalized and SAFE - does NOT block deletion
        // For WITHDRAWALS (request_type: 1): Status 1-4 are active
        // Status 5 (Paid) is finalized and SAFE
        let isActive = false;
        
        if (requestType === 0) {
          // DEPOSIT: Only status 0 (Pending) or 1 (Waiting) block deletion
          // Status 2 (Approved) is finalized - SAFE to delete
          // Status 3 (Cancelled), 4 (Rejected) are finalized - SAFE to delete
          isActive = status === 0 || status === 1;
        } else if (requestType === 1) {
          // WITHDRAWAL: Only status 1 (Processing) or 2 (Accepted) block deletion
          // Status 3 (Cancelled), 4 (Rejected), 5 (Paid) are finalized - SAFE to delete
          isActive = status === 1 || status === 2;
        } else {
          // Unknown type: Conservative approach - consider 1-4 as active
          isActive = status >= 1 && status <= 4;
        }
        
        // Get status label with full data context (now that we have requestType)
        const statusLabelInfo = getStatusLabel(status, { ...data, id: docSnapshot.id, request_type: requestType });
        
        const transaction = {
          id: docSnapshot.id,
          user_id: userId,
          user_name: isValidUserId ? getUserName(userId) : (userId !== "N/A" ? `Invalid ID: ${userId}` : "N/A"),
          total_amount: data.total_amount || 0,
          request_status: status,
          request_type: requestType,
          date: date ? date.toISOString() : (data.date || "N/A"),
          dateObj: date,
          statusLabel: statusLabelInfo,
          typeLabel: getTypeLabel(data.request_type),
          isOld: !isRecent, // Categorization for display (before 12/2024 = "Old")
          isActive: isActive, // Correctly determined based on request_type
          collection: "transection_requests",
          hasLedger: hasRelatedLedger,
          isValidUserId: isValidUserId, // Flag to help identify invalid user IDs
          rawData: data, // Store raw data for debugging and status checking
        };
        
        processedTransactions.push(transaction);
        
        // Track active transactions for safety check - only truly active ones block deletion
        if (isActive) {
          if (isRecent) {
            recentActive.push(transaction);
          } else {
            oldStuck.push(transaction);
          }
        }
      });
      
      // Fetch missing users dynamically (including priority users and all missing IDs)
      if (missingUserIds.size > 0 || true) { // Always try to fetch missing users, including searching all users
        console.log(`🔍 Fetching ${missingUserIds.size} missing users (plus searching all users for matches)...`);
        const newMap = new Map(usersMap);
        let fetchedCount = 0;
        
        // Priority users mentioned by user (known IDs that might not be found initially)
        const priorityUsers = [
          { id: 'igh16uiOxOhd2DzcDeKOmUHTbcp2', name: 'Luca Governo' },
          { id: 'xcsoLsBmp3NO5SgL0akEuBqQWCN2', name: 'Francesco Bono' },
          { id: 'wjrKNs3JZgStMCfmLdUO4JDhjwE2', name: 'Marco Antonio Agnesini de Amaral' }, // Found in table!
          { id: 'yGWMgVOKFMUBJhqTSKUR', name: 'Mauri Toniolo' },
          { id: 'Sw9WBW7xrLhDZcKym2aUA8121GO2', name: null }, // User ID for Mauri
          { id: 'GLSLmb8pTnafjtXj0PvTOsVlqYF2', name: 'CLAUDNEY CARNEIRO CARVALHO' },
          { id: 'fF3236Sde2fmGq82HvF7', name: 'CLAUDNEY CARNEIRO CARVALHO' }, // Document ID
          { id: 'M8VkGWTm92ToD1qy0ZsKoZ3vMk52', name: 'Sachin Dobariya' }
        ];
        
        // Add all missing user IDs to priority list for fetching
        missingUserIds.forEach(missingId => {
          if (!priorityUsers.some(p => p.id === missingId)) {
            priorityUsers.push({ id: missingId, name: null });
          }
        });
        
        // First, try direct lookups for all priority users
        for (const priority of priorityUsers) {
          if (missingUserIds.has(priority.id) || !newMap.has(priority.id)) {
            try {
              const userDoc = await getDoc(doc(DATABASE, "users", priority.id));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                const userName = userData.full_name || userData.email || priority.name || priority.id;
                newMap.set(priority.id, userName);
                // Also map doc_id if different
                if (userData.doc_id && userData.doc_id !== priority.id) {
                  newMap.set(userData.doc_id, userName);
                }
                fetchedCount++;
                console.log(`   ✅ [PRIORITY] Found ${priority.id}: ${userName}`);
              }
            } catch (error) {
              console.warn(`   ⚠️ [PRIORITY] Error fetching ${priority.id}:`, error);
            }
          }
        }
        
        // Search ALL users collection for any matches (by ID, doc_id, name, email)
        console.log(`   🔍 Searching ALL users collection for missing IDs and name matches...`);
        try {
          const allUsersSnapshot = await getDocs(collection(DATABASE, "users"));
          console.log(`   📋 Found ${allUsersSnapshot.docs.length} users in database`);
          
          // Build comprehensive search map
          const searchTerms = [
            { id: 'igh16uiOxOhd2DzcDeKOmUHTbcp2', terms: ['luca', 'governo'] },
            { id: 'xcsoLsBmp3NO5SgL0akEuBqQWCN2', terms: ['francesco', 'bono', 'bonofranz'] },
            { id: 'wjrKNs3JZgStMCfmLdUO4JDhjwE2', terms: ['marco', 'antonio', 'agnesini', 'amaral'] },
            { id: 'Sw9WBW7xrLhDZcKym2aUA8121GO2', terms: ['mauri', 'toniolo'] },
            { id: 'GLSLmb8pTnafjtXj0PvTOsVlqYF2', terms: ['claudney', 'carneiro', 'carvalho'] },
            { id: 'M8VkGWTm92ToD1qy0ZsKoZ3vMk52', terms: ['sachin', 'dobariya'] }
          ];
          
          // Add search terms for any missing IDs without known names
          priorityUsers.forEach(p => {
            if (!searchTerms.some(s => s.id === p.id)) {
              if (p.name) {
                // If we know the name, use it for search
                const nameParts = p.name.toLowerCase().split(/\s+/).filter(part => part.length > 2);
                searchTerms.push({ id: p.id, terms: nameParts });
              } else {
                // Unknown name - will try direct ID match
                searchTerms.push({ id: p.id, terms: [] });
              }
            }
          });
          
          allUsersSnapshot.docs.forEach(d => {
            const data = d.data();
            const docId = d.id;
            const fullName = (data.full_name || '').toLowerCase();
            const email = (data.email || '').toLowerCase();
            const docIdField = (data.doc_id || '').toString();
            const userName = data.full_name || data.email || docId;
            
            // First: Direct ID matches (doc.id, data.doc_id)
            searchTerms.forEach(({ id, terms }) => {
              if (!newMap.has(id) || newMap.get(id) === id) {
                // Check direct ID match
                if (docId === id || docIdField === id) {
                  newMap.set(id, userName);
                  newMap.set(docId, userName);
                  if (docIdField && docIdField !== docId) {
                    newMap.set(docIdField, userName);
                  }
                  console.log(`   ✅ [DIRECT ID] Mapped ${id} to ${userName} (from document ${docId})`);
                  fetchedCount++;
                } else if (terms.length > 0) {
                  // Check name/email match
                  const matches = terms.some(term => fullName.includes(term) || email.includes(term));
                  if (matches) {
                    newMap.set(id, userName);
                    newMap.set(docId, userName);
                    if (docIdField && docIdField !== docId) {
                      newMap.set(docIdField, userName);
                    }
                    console.log(`   ✅ [NAME/EMAIL] Mapped ${id} to ${userName} (from document ${docId}, matched: ${terms.join(', ')})`);
                    fetchedCount++;
                  }
                }
              }
            });
            
            // Also: Index this user by all possible identifiers
            if (userName !== docId) {
              newMap.set(docId, userName);
              if (docIdField && docIdField !== docId) {
                newMap.set(docIdField, userName);
              }
              if (email) {
                newMap.set(email.toLowerCase(), userName);
              }
            }
          });
          
          console.log(`   ✅ [SEARCH] Found ${fetchedCount} additional user mappings from comprehensive search`);
        } catch (error) {
          console.error(`   ❌ Error searching all users:`, error);
        }
        
        // Fetch remaining missing users (not in priority list)
        const userIdsToFetch = Array.from(missingUserIds)
          .filter(id => !priorityUsers.some(p => p.id === id))
          .slice(0, 100);
        
        for (const userId of userIdsToFetch) {
          try {
            const userDoc = await getDoc(doc(DATABASE, "users", userId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const userName = userData.full_name || userData.email || userId;
              newMap.set(userId, userName);
              // Also map doc_id if different
              if (userData.doc_id && userData.doc_id !== userId) {
                newMap.set(userData.doc_id, userName);
              }
              fetchedCount++;
            } else {
              newMap.set(userId, userId);
            }
          } catch (error) {
            console.error(`Error fetching user ${userId}:`, error);
            newMap.set(userId, userId);
          }
        }
        
        if (fetchedCount > 0 || newMap.size > usersMap.size) {
          setUsersMap(newMap);
          console.log(`✅ Updated users map (${newMap.size} total, ${fetchedCount} newly fetched)`);
          
          // Update user names in transactions
          processedTransactions.forEach(txn => {
            if (txn.user_id && newMap.has(txn.user_id)) {
              const newName = newMap.get(txn.user_id);
              if (newName && newName !== txn.user_id) {
                txn.user_name = newName;
              }
            }
          });
        }
      }

      // Sort by date (most recent first) initially
      processedTransactions.sort((a, b) => {
        if (!a.dateObj || !b.dateObj) return 0;
        return b.dateObj.getTime() - a.dateObj.getTime();
      });
      
      recentActive.sort((a, b) => {
        if (!a.dateObj || !b.dateObj) return 0;
        return b.dateObj.getTime() - a.dateObj.getTime();
      });
      
      oldStuck.sort((a, b) => {
        if (!a.dateObj || !b.dateObj) return 0;
        return b.dateObj.getTime() - a.dateObj.getTime();
      });

      setAllTransactions(processedTransactions);
      applyFilters(processedTransactions); // Apply initial filters

      // CRITICAL: Only consider RECENT truly active transactions as blocking deletion
      // For Deposits: Only status 0-1 block (status 2=Approved, 3=Cancelled, 4=Rejected are safe)
      // For Withdrawals: Only status 1-2 block (status 3=Cancelled, 4=Rejected, 5=Paid are safe)
      // Old transactions (before 12/2024) are shown but only recent ones block deletion
      console.log(`📊 [SUMMARY] Total transactions: ${processedTransactions.length}, Recent Active: ${recentActive.length}, Old Active (ignored): ${oldStuck.length}`);
      
      if (recentActive.length === 0) {
        setVerificationResult({
          safe: true,
          message: `✅ Safe to proceed: No recent active transactions found (from 12/2024 onwards). All transactions are displayed (including from 2022). Deposits with status 2+ (Approved/Cancelled/Rejected) and Withdrawals with status 3+ (Cancelled/Rejected/Paid) are finalized and safe. (${oldStuck.length} old active transactions from before 12/2024 are displayed but do not block deletion - these are likely test data)`,
          count: 0,
          oldStuckCount: oldStuck.length,
          recentActiveCount: 0,
          totalTransactions: processedTransactions.length,
        });
      } else {
        const depositActive = recentActive.filter(t => t.request_type === 0 && (t.request_status === 0 || t.request_status === 1)).length;
        const withdrawalActive = recentActive.filter(t => t.request_type === 1 && (t.request_status === 1 || t.request_status === 2)).length;
        
        setVerificationResult({
          safe: false,
          message: `❌ NOT SAFE: ${recentActive.length} recent active transaction(s) found (from 12/2024 onwards). Breakdown: ${depositActive} deposits (Pending/Waiting), ${withdrawalActive} withdrawals (Processing/Accepted). All transactions are displayed (including from 2022) for review. Deposits with status 2+ (Approved/Cancelled/Rejected) and Withdrawals with status 3+ (Cancelled/Rejected/Paid) are finalized and safe. (${oldStuck.length} old active transactions from before 12/2024 are displayed but do not block deletion)`,
          count: recentActive.length,
          oldStuckCount: oldStuck.length,
          recentActiveCount: recentActive.length,
          depositActiveCount: depositActive,
          withdrawalActiveCount: withdrawalActive,
          totalTransactions: processedTransactions.length,
        });
      }
    } catch (error) {
      console.error("Error verifying transactions:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to verify transactions. Please check console for details.",
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Apply filters and sorting
  const applyFilters = (transactions) => {
    // Must receive transactions parameter - don't use allTransactions state here
    if (!transactions || !Array.isArray(transactions) || transactions.length === 0) {
      console.log(`⚠️ [FILTER] No transactions to filter (transactions parameter is empty)`);
      setFilteredTransactions([]);
      return;
    }
    
    let filtered = [...transactions];
    
    console.log(`🔍 [FILTER] Starting with ${filtered.length} transactions`, {
      statusFilter,
      typeFilter,
      collectionFilter,
      searchText: searchText || '(empty)',
      sortBy,
      sortOrder,
      sourceCount: transactions.length
    });
    
    // Filter by status
    if (statusFilter !== "all" && statusFilter !== null && statusFilter !== undefined) {
      if (statusFilter === "active") {
        // Use isActive flag (correctly calculated based on request_type)
        filtered = filtered.filter(t => t.isActive === true);
        console.log(`   [FILTER] After status=active: ${filtered.length} transactions`);
      } else {
        // Filter by specific status - normalize both to numbers for comparison
        const targetStatus = Number(statusFilter);
        filtered = filtered.filter(t => {
          const txnStatus = Number(t.request_status);
          const matches = txnStatus === targetStatus;
          return matches;
        });
        console.log(`   [FILTER] After status=${statusFilter}: ${filtered.length} transactions`);
      }
    }
    
    // Filter by type
    if (typeFilter !== "all" && typeFilter !== null && typeFilter !== undefined) {
      const targetType = Number(typeFilter);
      filtered = filtered.filter(t => {
        const txnType = t.request_type !== null && t.request_type !== undefined ? Number(t.request_type) : null;
        return txnType === targetType;
      });
      console.log(`   [FILTER] After type=${typeFilter}: ${filtered.length} transactions`);
    }
    
    // Filter by collection (all are transection_requests, but show if has related ledger)
    if (collectionFilter !== "all" && collectionFilter !== null && collectionFilter !== undefined) {
      if (collectionFilter === "with_ledger") {
        filtered = filtered.filter(t => t.hasLedger === true);
      } else if (collectionFilter === "without_ledger") {
        filtered = filtered.filter(t => !t.hasLedger);
      }
      console.log(`   [FILTER] After collection=${collectionFilter}: ${filtered.length} transactions`);
    }
    
    // Search by user name or ID
    if (searchText && searchText.trim() !== "") {
      const searchLower = searchText.toLowerCase().trim();
      filtered = filtered.filter(t => {
        const userName = (t.user_name || "").toLowerCase();
        const userId = (t.user_id || "").toLowerCase();
        const userMatch = userName.includes(searchLower) || userId.includes(searchLower);
        return userMatch;
      });
      console.log(`   [FILTER] After search="${searchText}": ${filtered.length} transactions`);
    }
    
    // Sort
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      try {
        switch (sortBy) {
          case "userName":
            aValue = (a.user_name || "").toString();
            bValue = (b.user_name || "").toString();
            return sortOrder === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
          case "amount":
            aValue = Number(a.total_amount || 0);
            bValue = Number(b.total_amount || 0);
            return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
          case "status":
            aValue = Number(a.request_status || 0);
            bValue = Number(b.request_status || 0);
            return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
          case "date":
          default:
            aValue = a.dateObj ? a.dateObj.getTime() : 0;
            bValue = b.dateObj ? b.dateObj.getTime() : 0;
            return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
        }
      } catch (error) {
        console.error(`❌ [FILTER] Error sorting:`, error, { sortBy, sortOrder, a, b });
        return 0;
      }
    });
    
    console.log(`✅ [FILTER] Final filtered count: ${filtered.length} transactions`);
    setFilteredTransactions(filtered);
  };
  
  // Update filters and re-apply when filters change or transactions are loaded
  useEffect(() => {
    if (allTransactions && allTransactions.length > 0) {
      console.log(`🔄 [EFFECT] Re-applying filters to ${allTransactions.length} transactions`);
      applyFilters(allTransactions);
    } else if (allTransactions && allTransactions.length === 0) {
      // Clear filtered transactions if allTransactions is empty
      console.log(`🔄 [EFFECT] Clearing filtered transactions (allTransactions is empty)`);
      setFilteredTransactions([]);
    }
  }, [statusFilter, typeFilter, collectionFilter, searchText, sortBy, sortOrder, allTransactions]);
  
  // Note: We intentionally omit applyFilters from dependencies to avoid infinite loops
  // applyFilters is a stable function that uses current state values

  const getStatusLabel = (status, data = {}) => {
    const statusNum = Number(status);
    const requestType = data.request_type !== undefined ? Number(data.request_type) : null;
    
    // CRITICAL: Status mapping is DIFFERENT for Deposits vs Withdrawals!
    // Based on FundsDeposit.jsx and ViewLedgersModel.jsx:
    
    // For DEPOSITS (request_type: 0):
    // 0 = Pending, 1 = Waiting, 2 = Approved, 3 = Cancelled, 4 = Rejected
    
    // For WITHDRAWALS (request_type: 1):
    // 1 = Pending, 2 = Processing, 3 = Verified, 4 = Accepted, 5 = Paid
    
    // Debug specific document mentioned by user
    if (data.id === 'rlhMCzzm7eB0i1GlMQuX') {
      console.log(`🔍 [DEBUG] Document rlhMCzzm7eB0i1GlMQuX status check:`, {
        request_status: status,
        statusNum,
        request_type: requestType,
        approved: data.approved,
        is_approved: data.is_approved,
        notes: data.notes,
        request_approval_time: data.request_approval_time,
        allKeys: Object.keys(data),
        rawData: data
      });
    }
    
    // Deposit status mapping (request_type: 0)
    if (requestType === 0) {
      const depositStatusMap = {
        0: { label: "Pending", color: "warning" },
        1: { label: "Waiting", color: "info" },
        2: { label: "Approved", color: "success" }, // CRITICAL: Status 2 = Approved for deposits!
        3: { label: "Cancelled", color: "error" },
        4: { label: "Rejected", color: "error" },
        5: { label: "Paid", color: "success" }, // Rarely used for deposits
      };
      return depositStatusMap[statusNum] || { label: `Unknown (${status})`, color: "default" };
    }
    
    // Withdrawal status mapping (request_type: 1)
    // Based on WithdrowFunds.jsx:
    // Status 1 = Processing (or "Awaiting User Action" if profit_loss exists, but we show "Processing")
    // Status 2 = Accepted/Approved (admin accepted, but not yet paid) - STILL ACTIVE
    // Status 3 = Cancelled By User - FINALIZED (not active)
    // Status 4 = Rejected - FINALIZED (not active)
    // Status 5 = Paid - FINALIZED (not active)
    if (requestType === 1) {
      const withdrawalStatusMap = {
        0: { label: "Pending", color: "warning" },
        1: { label: "Processing", color: "info" }, // Or "Awaiting User Action" if profit_loss, but we simplify
        2: { label: "Accepted", color: "secondary" }, // Admin accepted, but not yet paid - STILL ACTIVE
        3: { label: "Cancelled By User", color: "error" }, // CRITICAL: NOT "Verified"!
        4: { label: "Rejected", color: "error" },
        5: { label: "Paid", color: "success" }, // CRITICAL: NOT "Verified"!
      };
      return withdrawalStatusMap[statusNum] || { label: `Unknown (${status})`, color: "default" };
    }
    
    // Performance/Other types (request_type: 2 or null)
    // Use generic mapping
    const genericStatusMap = {
      1: { label: "Pending", color: "warning" },
      2: { label: "Processing", color: "info" },
      3: { label: "Verified", color: "primary" },
      4: { label: "Accepted", color: "secondary" },
      5: { label: "Paid", color: "success" },
      6: { label: "Cancelled", color: "error" },
    };
    
    return genericStatusMap[statusNum] || { label: `Unknown (${status})`, color: "default" };
  };

  const getTypeLabel = (requestType) => {
    if (requestType === null || requestType === undefined) return "N/A";
    const typeMap = {
      0: { label: "Deposit", color: "success" },
      1: { label: "Withdrawal", color: "warning" },
      2: { label: "Performance", color: "info" },
    };
    return typeMap[requestType] || { label: `Type ${requestType}`, color: "default" };
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "N/A") return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const loadUsers = async () => {
    if (usersMap.size > 0) return; // Already loaded
    
    setLoadingUsers(true);
    try {
      console.log('🔄 Loading users from database...');
      const snapshot = await getDocs(collection(DATABASE, "users"));
      const map = new Map();
      
      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        const userName = data.full_name || data.email || doc.id;
        
        // CRITICAL: Index by document ID (primary key)
        map.set(doc.id, userName);
        
        // Also index by doc_id if it exists (some old documents use doc_id to reference users)
        if (data.doc_id && data.doc_id !== doc.id) {
          map.set(data.doc_id, userName);
        }
        
        // Also index by email as fallback (case-insensitive)
        if (data.email && typeof data.email === 'string') {
          map.set(data.email.toLowerCase(), userName);
        }
      });
      
      setUsersMap(map);
      console.log(`✅ Loaded ${snapshot.size} users into map`);
      
      // Debug: Check specific users mentioned by user
      const targetUsers = [
        { id: 'igh16uiOxOhd2DzcDeKOmUHTbcp2', name: 'Luca Governo' },
        { id: 'xcsoLsBmp3NO5SgL0akEuBqQWCN2', name: 'Francesco Bono' }
      ];
      
      targetUsers.forEach(target => {
        const foundUser = map.get(target.id);
        if (foundUser && foundUser !== target.id) {
          console.log(`✅ Found user ${target.id} (${target.name}): ${foundUser}`);
        } else {
          console.warn(`⚠️ User ${target.id} (${target.name}) NOT FOUND in users map`);
          
          // Search by name or email
          let foundByName = false;
          snapshot.docs.forEach(d => {
            const data = d.data();
            const fullName = (data.full_name || '').toLowerCase();
            const email = (data.email || '').toLowerCase();
            const searchTerms = target.name.toLowerCase().split(' ');
            const matchesName = searchTerms.every(term => fullName.includes(term) || email.includes(term));
            
            if (matchesName || 
                fullName.includes('luca') || fullName.includes('governo') || email.includes('luca') ||
                fullName.includes('francesco') || fullName.includes('bono') || email.includes('francesco') || email.includes('bonofranz')) {
              console.log(`   🔍 Found potential match for ${target.name}:`, {
                documentId: d.id,
                full_name: data.full_name,
                email: data.email,
                doc_id: data.doc_id,
                targetId: target.id
              });
              
              if (!foundByName) {
                const userName = data.full_name || data.email || d.id;
                map.set(target.id, userName); // Map target ID to found user's name
                map.set(d.id, userName); // Also map document ID
                if (data.doc_id && data.doc_id !== d.id) {
                  map.set(data.doc_id, userName); // Also map doc_id
                }
                foundByName = true;
                console.log(`   ✅ Mapped ${target.id} to ${userName} (from document ${d.id})`);
              }
            }
          });
          
          if (!foundByName) {
            console.error(`   ❌ ${target.name} (${target.id}) not found in ANY user document. Total users checked: ${snapshot.size}`);
          }
        }
      });
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const getUserName = (userId) => {
    if (!userId || userId === 'undefined' || userId === 'null') {
      return "N/A";
    }
    
    const normalizedUserId = String(userId).trim();
    if (!normalizedUserId) return "N/A";
    
    const userName = usersMap.get(normalizedUserId);
    if (userName && userName !== normalizedUserId) {
      return userName;
    }
    
    // Return userId if not found (helps debug missing users)
    return normalizedUserId || "N/A";
  };
  
  // Load users when component mounts
  useEffect(() => {
    loadUsers();
  }, []);

  // Handle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc"); // Default to desc
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, color: "#fff", fontWeight: "bold" }}>
        Safe Delete Verification Tool
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          <strong>Purpose:</strong> Verify if it's safe to delete collections by checking for active transactions.
        </Typography>
        <Typography variant="body2">
          This tool checks for <strong>active transactions</strong> that would block deletion.
          <br />
          <strong>Active transactions (block deletion):</strong>
          <br />
          • <strong>Deposits (Type 0):</strong> Status 0 (Pending) or 1 (Waiting)
          <br />
          • <strong>Withdrawals (Type 1):</strong> Status 1 (Processing) or 2 (Accepted)
          <br />
          <strong>Finalized transactions (safe - won't block):</strong>
          <br />
          • <strong>Deposits:</strong> Status 2 (Approved), 3 (Cancelled), 4 (Rejected) ✅
          <br />
          • <strong>Withdrawals:</strong> Status 3 (Cancelled By User), 4 (Rejected), 5 (Paid) ✅
          <br />
          <strong>⚠️ You can ONLY delete collections if there are NO active transactions.</strong>
        </Typography>
      </Alert>

      <Box sx={{ mb: 3, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
        <Button
          variant="contained"
          onClick={verifySafeToDelete}
          disabled={loading || loadingUsers}
          startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          sx={{ backgroundColor: "#2af598", color: "#000" }}
          size="large"
        >
          {loading ? "Verifying..." : "Check Active Transactions"}
        </Button>
        {loadingUsers && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" sx={{ color: "#aaa" }}>
              Loading user names...
            </Typography>
          </Box>
        )}
        {usersMap.size > 0 && !loadingUsers && (
          <Typography variant="body2" sx={{ color: "#2af598" }}>
            ✅ {usersMap.size} users loaded
          </Typography>
        )}
      </Box>

      {verificationResult && (
        <Box sx={{ mb: 3 }}>
          <Card>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                    {verificationResult.safe ? (
                      <>
                        <CheckCircleIcon sx={{ color: "#2af598" }} />
                        Verification Result
                      </>
                    ) : (
                      <>
                        <ErrorIcon sx={{ color: "#ff5722" }} />
                        Verification Result
                      </>
                    )}
                  </Typography>
                  <Typography variant="body1" sx={{ color: verificationResult.safe ? "#2af598" : "#ff5722" }}>
                    {verificationResult.message}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: "#aaa" }}>
                    Recent Active (12/2024+): <strong>{verificationResult.recentActiveCount || 0}</strong>
                    {verificationResult.oldStuckCount > 0 && (
                      <>
                        <br />
                        Old Stuck (before 12/2024): <strong style={{ color: '#ff9800' }}>{verificationResult.oldStuckCount}</strong> (ignored)
                      </>
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  {verificationResult.safe ? (
                    <Alert severity="success" icon={<CheckCircleIcon />}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        ✅ SAFE TO PROCEED
                      </Typography>
                      <Typography variant="caption">
                        You can proceed with deleting and reimporting collections.
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert severity="error" icon={<ErrorIcon />}>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        ❌ NOT SAFE TO PROCEED
                      </Typography>
                      <Typography variant="caption">
                        You must wait for all active transactions to complete (status 5 or 6) before deleting.
                      </Typography>
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>
      )}

      {filteredTransactions.length > 0 && (
        <Box>
          {/* Filters and Search */}
          <Card sx={{ mb: 2, backgroundColor: "#1a1a1a" }}>
            <CardContent>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search by User Name or ID"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        color: "#fff",
                        "& fieldset": { borderColor: "#444" },
                      },
                      "& .MuiInputLabel-root": { color: "#aaa" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ color: "#aaa" }}>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        console.log(`🔄 [FILTER] Status filter changed to:`, newValue);
                        setStatusFilter(newValue);
                      }}
                      sx={{
                        color: "#fff",
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2af598" },
                      }}
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="active">Active Only</MenuItem>
                      <MenuItem value="0">Status 0 (Pending/Waiting)</MenuItem>
                      <MenuItem value="1">Status 1 (Waiting/Processing)</MenuItem>
                      <MenuItem value="2">Status 2 (Approved/Accepted)</MenuItem>
                      <MenuItem value="3">Status 3 (Cancelled)</MenuItem>
                      <MenuItem value="4">Status 4 (Rejected)</MenuItem>
                      <MenuItem value="5">Status 5 (Paid)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ color: "#aaa" }}>Type</InputLabel>
                    <Select
                      value={typeFilter}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        console.log(`🔄 [FILTER] Type filter changed to:`, newValue);
                        setTypeFilter(newValue);
                      }}
                      sx={{
                        color: "#fff",
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                      }}
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      <MenuItem value="0">Deposit (Type 0)</MenuItem>
                      <MenuItem value="1">Withdrawal (Type 1)</MenuItem>
                      <MenuItem value="2">Performance (Type 2)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ color: "#aaa" }}>Collection</InputLabel>
                    <Select
                      value={collectionFilter}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        console.log(`🔄 [FILTER] Collection filter changed to:`, newValue);
                        setCollectionFilter(newValue);
                      }}
                      sx={{
                        color: "#fff",
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                      }}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="with_ledger">With Ledger</MenuItem>
                      <MenuItem value="without_ledger">Without Ledger</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel sx={{ color: "#aaa" }}>Sort By</InputLabel>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      sx={{
                        color: "#fff",
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                      }}
                    >
                      <MenuItem value="date">Date</MenuItem>
                      <MenuItem value="userName">User Name</MenuItem>
                      <MenuItem value="amount">Amount</MenuItem>
                      <MenuItem value="status">Status</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Typography variant="body2" sx={{ color: "#aaa" }}>
                Showing {filteredTransactions.length} of {allTransactions.length} transactions
                {statusFilter === "active" && ` (${verificationResult?.recentActiveCount || 0} recent active)`}
              </Typography>
            </CardContent>
          </Card>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#2a2a2a" }}>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Document ID</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    <TableSortLabel
                      active={sortBy === "userName"}
                      direction={sortBy === "userName" ? sortOrder : "asc"}
                      onClick={() => handleSort("userName")}
                      sx={{ color: "#fff !important", "& .MuiTableSortLabel-icon": { color: "#fff !important" } }}
                    >
                      User Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>User ID</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    <TableSortLabel
                      active={sortBy === "status"}
                      direction={sortBy === "status" ? sortOrder : "asc"}
                      onClick={() => handleSort("status")}
                      sx={{ color: "#fff !important", "& .MuiTableSortLabel-icon": { color: "#fff !important" } }}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Type</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    <TableSortLabel
                      active={sortBy === "amount"}
                      direction={sortBy === "amount" ? sortOrder : "asc"}
                      onClick={() => handleSort("amount")}
                      sx={{ color: "#fff !important", "& .MuiTableSortLabel-icon": { color: "#fff !important" } }}
                    >
                      Amount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    <TableSortLabel
                      active={sortBy === "date"}
                      direction={sortBy === "date" ? sortOrder : "desc"}
                      onClick={() => handleSort("date")}
                      sx={{ color: "#fff !important", "& .MuiTableSortLabel-icon": { color: "#fff !important" } }}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Collection</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Category</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransactions.slice(0, 500).map((txn) => {
                  // Use stored statusLabel from transaction (already calculated with full data)
                  const statusInfo = txn.statusLabel || getStatusLabel(txn.request_status, txn.rawData || {});
                  const typeInfo = txn.typeLabel || getTypeLabel(txn.request_type);
                  
                  // Debug specific transaction mentioned by user
                  if (txn.id === 'rlhMCzzm7eB0i1GlMQuX') {
                    console.log(`🔍 [RENDER] Transaction rlhMCzzm7eB0i1GlMQuX details:`, {
                      id: txn.id,
                      request_status: txn.request_status,
                      request_type: txn.request_type,
                      statusLabel: statusInfo,
                      approved: txn.rawData?.approved,
                      is_approved: txn.rawData?.is_approved,
                      notes: txn.rawData?.notes,
                      allRawDataKeys: Object.keys(txn.rawData || {}),
                      user_name: txn.user_name,
                      user_id: txn.user_id,
                      rawData: txn.rawData
                    });
                  }
                  
                  return (
                    <TableRow 
                      key={txn.id}
                      sx={{ 
                        backgroundColor: txn.isOld ? 'rgba(255, 152, 0, 0.05)' : 'inherit',
                        opacity: txn.isOld ? 0.7 : 1
                      }}
                    >
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                        {txn.id}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "medium", color: "#fff" }}>
                        {txn.user_name && txn.user_name !== txn.user_id ? (
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            {txn.user_name}
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: "#ff9800", fontStyle: "italic" }}>
                            User not found
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem", color: "#aaa" }}>
                        {txn.user_id}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={`Status Code: ${txn.request_status} | Type: ${txn.request_type}${txn.rawData?.approved ? ' | Approved flag: true' : ''}${txn.rawData?.notes ? ` | Notes: ${txn.rawData.notes.substring(0, 50)}` : ''}`}>
                          <Chip
                            label={statusInfo.label}
                            color={statusInfo.color}
                            size="small"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {typeInfo.label !== "N/A" ? (
                          <Chip
                            label={typeInfo.label}
                            color={typeInfo.color}
                            size="small"
                          />
                        ) : (
                          <Typography variant="caption" sx={{ color: "#aaa", fontStyle: "italic" }}>
                            N/A
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>${Number(txn.total_amount).toFixed(2)}</TableCell>
                      <TableCell sx={{ fontSize: "0.85rem" }}>{formatDate(txn.date)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={txn.collection} 
                          size="small"
                          sx={{ fontSize: "0.7rem", backgroundColor: "#444", color: "#fff" }}
                        />
                        {txn.hasLedger && (
                          <Tooltip title="Has related ledger entry">
                            <Chip 
                              label="+ Ledger" 
                              size="small"
                              sx={{ fontSize: "0.65rem", ml: 0.5, backgroundColor: "#2af598", color: "#000" }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        {txn.isOld ? (
                          <Chip 
                            label="Old (Ignored)" 
                            color="warning" 
                            size="small"
                            sx={{ fontSize: "0.7rem" }}
                          />
                        ) : (
                          <Chip 
                            label={txn.isActive ? "Recent Active" : "Recent Finalized"} 
                            color={txn.isActive ? "error" : "success"} 
                            size="small"
                            sx={{ fontSize: "0.7rem" }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {filteredTransactions.length > 500 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Showing first 500 results. Use filters to narrow down the list.
              </Typography>
            </Alert>
          )}
          {verificationResult && verificationResult.oldStuckCount > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> Old stuck transactions (before 12/2024) are shown in orange/yellow. 
                These are historical data issues from the old system and are <strong>NOT</strong> considered 
                active transactions. Only recent transactions (from 12/2024 onwards) with status 1-4 block deletion.
              </Typography>
            </Alert>
          )}
        </Box>
      )}

      {verificationResult && verificationResult.safe && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            ✅ Next Steps (Safe to Proceed)
          </Typography>
          <Typography variant="body2" component="div">
            <ol>
              <li>✅ Make a complete backup of all collections (export to JSON/CSV)</li>
              <li>✅ Proceed with delete and reimport in correct order:
                <ul>
                  <li>Delete: Notifications → Ledgers → Monthly Graphs → Monthly Closings → Transaction Requests</li>
                  <li>Reimport: Transaction Requests → Ledgers → Monthly Closings → Monthly Graphs → Notifications</li>
                </ul>
              </li>
              <li>✅ Use Production Migration Tool to reimport from Production</li>
              <li>✅ Verify that dates are preserved correctly from Production</li>
            </ol>
          </Typography>
        </Alert>
      )}

      {verificationResult && !verificationResult.safe && (
        <Alert severity="error" sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            ❌ What to Do (NOT Safe to Proceed)
          </Typography>
          <Typography variant="body2" component="div">
            <ol>
              <li>⚠️ <strong>DO NOT delete collections</strong> while there are active transactions</li>
              <li>⚠️ Wait for all active transactions to be finalized (status 5 = Paid, or 6 = Cancelled)</li>
              <li>⚠️ If transactions are stuck, manually review and update their status in the Admin Panel</li>
              <li>⚠️ Run this verification again after all transactions are finalized</li>
            </ol>
            <Typography variant="body2" sx={{ mt: 2, fontStyle: "italic" }}>
              Deleting collections with active transactions will break the system and cause data loss.
            </Typography>
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default SafeDeleteVerificationTool;
